// One rule throughout: an event fires once per thing that happened.
// Abandonment is the easy one to get wrong, because a user can leave a search
// several times in one sitting and each departure is one abandonment, not one
// per keystroke and not one per render.

import { useCallback, useMemo, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import {
  carrierSelectedAttributes,
  leftRegistryAttributes,
  searchAbandonedAttributes,
  searchResultsAttributes,
  type LeftRegistry,
  type SearchOutcome,
} from './searchTelemetry'

type Attributes = Record<string, unknown>

export interface CarrierSearchTelemetry {
  /** Any event on this surface, stamped with the component and surface. */
  track: (event: string, attributes: Attributes) => void
  searchResults: (outcome: SearchOutcome) => void
  carrierSelected: (
    params: { rank: number; dotNumber?: string | null },
    extra?: Attributes
  ) => void
  manualEntry: (params: LeftRegistry, extra?: Attributes) => void
  /** Called when a search is left behind. Fires once per interaction, or not at all. */
  searchAbandoned: () => void
  /** The search ended some other way; whatever happens next, it was not abandoned. */
  resolved: () => void
}

export const useCarrierSearchTelemetry = ({
  component,
  surface,
}: {
  component: string
  surface: string
}): CarrierSearchTelemetry => {
  // What the current search interaction looks like, and whether it has already
  // been counted as abandoned.
  const search = useRef<{ query: string; resultsCount: number } | null>(null)
  const abandonReported = useRef(false)

  const track = useCallback(
    (event: string, attributes: Attributes) => {
      datadogRum.addAction(event, { component, surface, ...attributes })
    },
    [component, surface]
  )

  const searchResults = useCallback(
    (outcome: SearchOutcome) => {
      search.current = outcome
      // A new result set means a live interaction again, so a later departure
      // is a fresh abandonment rather than a repeat of the last one.
      abandonReported.current = false
      track('fmcsa_search_results', searchResultsAttributes(outcome))
    },
    [track]
  )

  const resolved = useCallback(() => {
    search.current = null
    abandonReported.current = true
  }, [])

  const carrierSelected = useCallback<
    CarrierSearchTelemetry['carrierSelected']
  >(
    (params, extra = {}) => {
      resolved()
      track('fmcsa_carrier_selected', {
        ...carrierSelectedAttributes(params),
        ...extra,
      })
    },
    [resolved, track]
  )

  const manualEntry = useCallback<CarrierSearchTelemetry['manualEntry']>(
    (params, extra = {}) => {
      resolved()
      track('fmcsa_manual_entry', {
        ...leftRegistryAttributes(params),
        ...extra,
      })
    },
    [resolved, track]
  )

  const searchAbandoned = useCallback(() => {
    const current = search.current
    if (!current || abandonReported.current) return
    abandonReported.current = true
    track('fmcsa_search_abandoned', searchAbandonedAttributes(current))
  }, [track])

  return useMemo(
    () => ({
      track,
      searchResults,
      carrierSelected,
      manualEntry,
      searchAbandoned,
      resolved,
    }),
    [
      track,
      searchResults,
      carrierSelected,
      manualEntry,
      searchAbandoned,
      resolved,
    ]
  )
}
